import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import Section from './components/Section'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE, hasNonZeroValue } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_MOBILE_DEVICES } from './operations-gql/queries'
import { CountPoint, MobileDevicesQuery, ProductAnalyticsPeriod, ProductAnalyticsVariables } from './types'

type MobileDevicesSectionProps = {
  period: ProductAnalyticsPeriod
}

type SessionsChartProps = {
  title: string
  description: string
  data: CountPoint[] | null | undefined
  color: string
  loading: boolean
  hasError: boolean
}

const TRANSLATION_PREFIX = 'stats.product_analytics.devices'
const UNKNOWN_LABEL = 'unknown'

const SessionsChart: React.FC<SessionsChartProps> = ({ title, description, data, color, loading, hasError }) => {
  const { t } = useTranslation()

  const rows = useMemo(() => (
    data?.map(({ label, value }) => ({ label: label === UNKNOWN_LABEL ? t(`${TRANSLATION_PREFIX}.unknown`) : label, value })) ?? []
  ), [data, t])

  return (
    <ChartCard
      title={title}
      description={description}
      loading={loading}
      hasError={hasError}
      isEmpty={!hasNonZeroValue(data, ['value'])}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows}>
          <CartesianGrid vertical={false} stroke="#F3F4F6" />
          <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} interval={0} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
          <Bar
            dataKey="value"
            name={t(`${TRANSLATION_PREFIX}.sessions`)}
            fill={color}
            radius={[6, 6, 0, 0]}
            barSize={40}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

const MobileDevicesSection: React.FC<MobileDevicesSectionProps> = ({ period }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<MobileDevicesQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_MOBILE_DEVICES,
    { variables: { period }, errorPolicy: 'all' }
  )
  const sessionsByOs = data?.productAnalytics?.sessionsByOs
  const sessionsByAppVersion = data?.productAnalytics?.sessionsByAppVersion

  return (
    <Section id="product-analytics-mobile-devices" title={t(`${TRANSLATION_PREFIX}.title`)} columns={{ xs: 1, md: 2 }}>
      <SessionsChart
        title={t(`${TRANSLATION_PREFIX}.os`)}
        description={t(`${TRANSLATION_PREFIX}.os_description`)}
        data={sessionsByOs}
        color={CHART_COLORS.primary}
        loading={loading}
        hasError={Boolean(error) && !sessionsByOs}
      />
      <SessionsChart
        title={t(`${TRANSLATION_PREFIX}.app_versions`)}
        description={t(`${TRANSLATION_PREFIX}.app_versions_description`)}
        data={sessionsByAppVersion}
        color={CHART_COLORS.quaternary}
        loading={loading}
        hasError={Boolean(error) && !sessionsByAppVersion}
      />
    </Section>
  )
}

export default MobileDevicesSection
